import { Check, ChevronDown } from 'lucide-react'

import { Logo, SYMBOL_ONLY, SYMBOL_VIEWBOX } from '@/components/brand/Logo'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useAgentName } from './agentName'
import { ProductTab, PROTOTYPE_TABS, TAB_LABEL } from './prototypeNav'
import { SignedInAvatar } from './SignedInAvatar'

interface FleetTopNavProps {
  tab: ProductTab
  onSelect: (tab: ProductTab) => void
  orgName: string
  /** The agent's own landing page, which stands outside the products. */
  agentActive: boolean
  onAgent: () => void
  /** Drops the wordmark, leaving the symbol — for options that say Cai elsewhere. */
  symbolOnlyLogo?: boolean
  /** Hidden where the agent has no page of its own and lives inside the tabs. */
  agentTab?: boolean
  /** The product that is not finished yet, flagged in the bar. */
  betaTab?: ProductTab
  /** Which products this account is shown; all of them unless narrowed. */
  tabs?: ProductTab[]
  /** What the agent's own tab is called, where that is not just its name. */
  agentTabLabel?: string
  /** Renames products for this bar only; the rest of the page keeps TAB_LABEL. */
  tabLabels?: Partial<Record<ProductTab, string>>
  /** Merged onto the bar, for a mock whose chrome is not white. */
  className?: string
  /** Replaces the plain avatar, where the account menu does something. */
  profile?: React.ReactNode
}

export const LINK_CLASS = '-mb-px border-b-2 px-2 py-3 text-xs'
export const ACTIVE_CLASS = 'border-brand-700 text-ink font-semibold'
export const IDLE_CLASS = 'text-ink-500 hover:text-ink border-transparent'

/** The other fleet this account belongs to, so the switcher has somewhere to go. */
const OTHER_ORG = 'BL Refrigerated Transport'

/**
 * The mark in black. `Logo`'s symbolOnly is hardcoded to the brand green, so a
 * black one has to come from the exported paths — the same way AskCaiDrawer
 * tints it in the app.
 */
export function BlackMark() {
  return (
    <svg
      className="text-ink block size-[22px] flex-none"
      viewBox={SYMBOL_VIEWBOX}
      role="img"
      aria-label="Cai"
    >
      {SYMBOL_ONLY.map((d) => (
        <path key={d} d={d} fill="currentColor" />
      ))}
    </svg>
  )
}

/** The products as a top bar — the side nav's job moved up and across. */
export function FleetTopNav({
  tab,
  onSelect,
  orgName,
  agentActive,
  onAgent,
  symbolOnlyLogo,
  agentTab = true,
  betaTab,
  tabs = PROTOTYPE_TABS,
  agentTabLabel,
  tabLabels,
  className,
  profile,
}: FleetTopNavProps) {
  const agentName = useAgentName()

  return (
    // Three tracks, so the products stay centred on the bar whatever flanks them.
    <div
      className={cn(
        'border-ink-100 grid grid-cols-[1fr_auto_1fr] items-center gap-4 border-b bg-white px-4',
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {symbolOnlyLogo ? <BlackMark /> : <Logo size="small" width={44} />}
        {/* A label with a way out of it, not a destination: there is no account
            page under the carrier's name once the dashboard is gone. */}
        <DropdownMenu>
          <DropdownMenuTrigger className="hover:bg-sidebar-accent flex min-w-0 items-center gap-1 rounded-md px-2 py-1">
            <span className="text-ink-900 truncate text-sm font-medium capitalize">{orgName}</span>
            <ChevronDown className="text-ink-300 size-3 shrink-0" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {[orgName, OTHER_ORG].map((name) => (
              <DropdownMenuItem key={name} className="gap-2 text-xs capitalize">
                <Check className={cn('size-3.5', name !== orgName && 'invisible')} />
                {name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center gap-1">
        {agentTab && (
          <>
            {/* The agent leads the products: it is the whole pitch of this option. */}
            <button
              type="button"
              onClick={onAgent}
              aria-current={agentActive ? 'page' : undefined}
              className={cn(LINK_CLASS, agentActive ? ACTIVE_CLASS : IDLE_CLASS)}
            >
              {agentTabLabel ?? agentName}
            </button>
            {/* The agent is a different kind of destination from the products. */}
            <span aria-hidden className="bg-ink-100 mx-1 h-4 w-px" />
          </>
        )}
        {tabs.map((entry) => {
          const active = !agentActive && entry === tab
          return (
            <button
              key={entry}
              type="button"
              onClick={() => onSelect(entry)}
              aria-current={active ? 'page' : undefined}
              className={cn(LINK_CLASS, active ? ACTIVE_CLASS : IDLE_CLASS)}
            >
              {tabLabels?.[entry] ?? TAB_LABEL[entry]}
              {betaTab === entry && (
                <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[9px] font-medium">
                  Beta
                </Badge>
              )}
            </button>
          )
        })}
      </div>

      <div className="flex justify-end">{profile ?? <SignedInAvatar />}</div>
    </div>
  )
}
